/**
 * L.3: single-file pane state.
 *
 * The `WorkspaceFilePane` opens ONE file at a time (layout-plan §3.5 —
 * "Single-file-at-a-time. No tabs. Opening a different file replaces
 * the current content."). This store is the source of truth for which
 * file that is; the pane renders iff `activeFile != null`.
 *
 * Persistence: intentionally ephemeral (layout-plan §8 table). Closing
 * a browser tab, navigating away from a case, or hitting `[×]` all
 * reset the pane. The selected file never leaks across cases — enforced
 * TWICE, because the two vectors differ: `WorkspaceFileUrlSync` closes on
 * URL navigation, and `WorkspaceFilePaneShell` closes on a selection
 * caseId change WITHOUT a navigation (the vector that leaked: a mounted
 * viewer re-read its case-relative path against the NEW case on every
 * reconnect — bus `…-j3nw`, 2026-08-20).
 *
 * Opening a file does NOT change the active selection (layout-plan §4.2
 * "Not entry points: Opening a file in WorkspaceFilePane does NOT
 * change selection"). Keep that invariant if you ever add a helper
 * that calls openFile — don't pair it with a selection mutation.
 *
 * See: ai-docs/lexide-layout-plan.md §3.5, §8
 */

import { create } from "zustand";

/** A reference to an openable file inside the active case's workspace. */
export interface FileRef {
  /** Zone-relative path, e.g., "07_drafts/contestacao-trabalhista.md". */
  path: string;
  /** Optional human-friendly title; falls back to the basename of `path`. */
  title?: string;
  /** Present when the file is AIEditor-tracked; future phases use this
   *  to dispatch to the right viewer chrome (read-only vs. prompt-box). */
  documentType?: string;
  /** Case-scoped UUID from the AIEditor `DocumentMeta`, when known. */
  docId?: string;
  /**
   * How the opener wants the file PRESENTED (plan §6 F2). `"extraction"` asks
   * the extraction reader to mount with the full machine extraction already
   * expanded, which is what the case-files card's "Ver extração" means as
   * distinct from its "Visualizar".
   *
   * A HINT, never a dispatch key: the viewer is still chosen by extension, so
   * a viewer that does not understand the hint simply ignores it and opens
   * normally. Absent = the default presentation.
   */
  intent?: "extraction";
  /**
   * The file this one was opened FROM, when the lawyer followed a link out of
   * a document rather than picking the file directly.
   *
   * ⚖ **Recursive on purpose — the chain IS the history.** A→B→C needs no
   * second stack: "back" from C lands on a B that still remembers A, and each
   * hop is exactly the ref that was showing, so returning restores presentation
   * (`intent`, `title`, `scrollTop`) and not merely a path.
   *
   * Absent means "opened directly" — from the tree, the Acervo, an attachment
   * card, `lx_open_file`. That is the truth, not a missing value, and the back
   * affordance is suppressed on it: an always-present back button that
   * sometimes does nothing is a lie about where the lawyer has been.
   */
  openedFrom?: FileRef;
  /**
   * Where the reader was in THIS file, in pixels, when they followed a link
   * out of it. Only meaningful on a scrolling viewer (markdown today).
   *
   * Recorded at the moment of leaving, because "return to the conversation"
   * means the message they were reading, not the top of a 128 KB page.
   */
  scrollTop?: number;
}

/**
 * How many ANCESTORS a provenance chain may keep — so a capped chain holds this
 * many refs *plus* the head, i.e. 11 refs at the default, and offers 10 back
 * steps. Named for the hops it preserves, which is the number that matters to
 * the lawyer.
 *
 * ⚠ Cycles are REACHABLE, not hypothetical: a generated conversation page links
 * to its transcript companion, which is itself a linked markdown document, so
 * A→B→A→B… is an ordinary thing for a curious reader to do. An uncapped chain
 * would grow forever while looking perfectly healthy — every ref valid, every
 * back step working, memory climbing.
 *
 * Ten is far past any real reading path and cheap to hold.
 */
export const MAX_PROVENANCE_DEPTH = 10;

/**
 * Trim a provenance chain to `MAX_PROVENANCE_DEPTH`, dropping the FAR tail.
 *
 * The far end is the right thing to lose: the nearest hops are the ones the
 * lawyer is about to walk back, and the oldest is the one they are least likely
 * to want. The returned ref is a copy — this never mutates the caller's object,
 * which would edit a ref another component still holds.
 */
export function capProvenance(ref: FileRef, depth = MAX_PROVENANCE_DEPTH): FileRef {
  if (depth <= 0) {
    const { openedFrom: _dropped, ...rest } = ref;
    return rest;
  }
  if (!ref.openedFrom) return ref;
  return { ...ref, openedFrom: capProvenance(ref.openedFrom, depth - 1) };
}

interface WorkspaceFileStore {
  activeFile: FileRef | null;
  /** The element the user clicked to open the current file, if any.
   *  Used to return keyboard focus when the pane closes (layout-plan
   *  §7.5 "Esc returns focus to the trigger that opened the pane").
   *  Not part of the React render graph — stored for side-effect use. */
  trigger: HTMLElement | null;
  openFile: (ref: FileRef, trigger?: HTMLElement | null) => void;
  closeFile: () => void;
}

export const useWorkspaceFileStore = create<WorkspaceFileStore>((set, get) => ({
  activeFile: null,
  trigger: null,
  // Capping here, at the single write point, rather than at each call site —
  // a call site that forgets would grow the chain silently.
  openFile: (ref, trigger = null) => set({ activeFile: capProvenance(ref), trigger }),
  closeFile: () => {
    const priorTrigger = get().trigger;
    set({ activeFile: null, trigger: null });
    // Return focus to the element that opened the file, if it's still
    // in the DOM. Done outside `set` so React has finished unmounting
    // the pane before we refocus.
    if (priorTrigger && typeof document !== "undefined" && document.contains(priorTrigger)) {
      priorTrigger.focus();
    }
  },
}));

// DEV-only test accessor. The store is ephemeral (not persisted), so
// E2E tests can't observe it via sessionStorage like they do with
// `useActiveContextStore`. Expose a read-only handle under a known
// name so Playwright can assert the URL→store sync direction even
// when no `WorkspaceFilePane` is mounted to render the state. The
// expose is stripped from production bundles by Vite's dead-code
// elimination when `import.meta.env.DEV` is `false`.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__wfsStore", {
    value: useWorkspaceFileStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}

/** Extract the filename from a zone-relative path (e.g., "07_drafts/foo.md"
 *  → "foo.md"). Small, pure; kept here so consumers don't all re-derive it. */
export function basename(path: string): string {
  const idx = path.lastIndexOf("/");
  return idx >= 0 ? path.slice(idx + 1) : path;
}

/** Extract the file extension (lowercased, no leading dot), or "" when
 *  the path has none. Used by the viewer dispatch in `WorkspaceFilePane`. */
export function extension(path: string): string {
  const name = basename(path);
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}
